import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap';
import DataTable, { TableColumn } from 'react-data-table-component';
import { BsFileEarmarkExcel, BsSearch } from 'react-icons/bs';
import DTOReporteHorarioTE from '../../../DTO/Reportes/DTOReporteHorarioTE';
import reportesServices from '../../../Services/Reportes/reportes.services';
import { MsgInformativo } from '../../../Utils/Toast/msgInformativo';
import generarExcelReporteHorarioTE from './generarExcelReporteHorarioTE';

const MILISEGUNDOS_POR_DIA = 24 * 60 * 60 * 1000;
const DIAS_POR_CICLO = 52 * 7;
const CICLO_ANCLA = 2026;
const FECHA_ANCLA_UTC = Date.UTC(2026, 0, 1);
const SEMANAS = Array.from({ length: 52 }, (_, index) => index + 1);

const obtenerCicloSemanaActual = () => {
  const hoy = new Date();
  const hoyUtc = Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  const diasDesdeAncla = Math.floor(
    (hoyUtc - FECHA_ANCLA_UTC) / MILISEGUNDOS_POR_DIA
  );
  const desplazamientoCiclo = Math.floor(diasDesdeAncla / DIAS_POR_CICLO);
  const diaDentroCiclo =
    ((diasDesdeAncla % DIAS_POR_CICLO) + DIAS_POR_CICLO) %
    DIAS_POR_CICLO;

  return {
    ciclo: CICLO_ANCLA + desplazamientoCiclo,
    semana: Math.floor(diaDentroCiclo / 7) + 1,
  };
};

const valoresIniciales = obtenerCicloSemanaActual();

const formatearFecha = (fecha?: string | null) => {
  if (!fecha) return '—';

  const parteFecha = fecha.split('T')[0];
  const [anio, mes, dia] = parteFecha.split('-');

  if (!anio || !mes || !dia) return '—';
  return `${dia}/${mes}/${anio}`;
};

const obtenerMensajeError = (error: unknown) => {
  const errorHttp = error as {
    response?: {
      data?: string | { title?: string; message?: string };
      status?: number;
    };
    message?: string;
  };

  const data = errorHttp.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    return data.title || data.message || 'No se pudo consultar el reporte.';
  }
  return errorHttp.message || 'No se pudo consultar el reporte.';
};

const columnas: TableColumn<DTOReporteHorarioTE>[] = [
  {
    name: 'Ciclo',
    selector: (row) => row.ciclo,
    sortable: true,
    width: '85px',
  },
  {
    name: 'Semana',
    selector: (row) => row.semana,
    sortable: true,
    width: '95px',
  },
  {
    name: 'Inicio',
    selector: (row) => formatearFecha(row.fechaInicioSemana),
    sortable: true,
    sortFunction: (a, b) =>
      a.fechaInicioSemana.localeCompare(b.fechaInicioSemana),
    width: '115px',
  },
  {
    name: 'Fin',
    selector: (row) => formatearFecha(row.fechaFinSemana),
    sortable: true,
    sortFunction: (a, b) => a.fechaFinSemana.localeCompare(b.fechaFinSemana),
    width: '115px',
  },
  {
    name: 'No. empleado',
    selector: (row) => row.numEmpleado,
    sortable: true,
    width: '130px',
  },
  {
    name: 'Nombre',
    selector: (row) => row.nombreEmpleado,
    sortable: true,
    width: '240px',
  },
  {
    name: 'Departamento',
    selector: (row) => row.departamento,
    sortable: true,
    width: '190px',
  },
  {
    name: 'Oficina',
    selector: (row) => row.oficina,
    sortable: true,
    width: '150px',
  },
  {
    name: 'Retardos',
    selector: (row) => row.retardos,
    sortable: true,
    width: '105px',
  },
  {
    name: 'Faltas',
    selector: (row) => row.faltas,
    sortable: true,
    width: '90px',
  },
  {
    name: 'Min. TE',
    selector: (row) => row.minutosTiempoExtra,
    sortable: true,
    width: '100px',
  },
  {
    name: 'TE HH:MM',
    selector: (row) => row.tiempoExtraHHMM,
    sortable: true,
    width: '110px',
  },
  {
    name: 'Permisos',
    selector: (row) => row.permisos,
    sortable: true,
    width: '100px',
  },
  {
    name: 'Retardos acum.',
    selector: (row) => row.retardosAcumulado,
    sortable: true,
    width: '135px',
  },
  {
    name: 'Faltas acum.',
    selector: (row) => row.faltasAcumulado,
    sortable: true,
    width: '120px',
  },
  {
    name: 'Min. TE acum.',
    selector: (row) => row.minutosTiempoExtraAcumulado,
    sortable: true,
    width: '130px',
  },
  {
    name: 'TE acum. HH:MM',
    selector: (row) => row.tiempoExtraAcumuladoHHMM,
    sortable: true,
    width: '145px',
  },
  {
    name: 'Permisos acum.',
    selector: (row) => row.permisosAcumulado,
    sortable: true,
    width: '135px',
  },
];

export default function RptHorarioTEGeneral() {
  const [ciclo, setCiclo] = useState(valoresIniciales.ciclo);
  const [semanaInicio, setSemanaInicio] = useState(valoresIniciales.semana);
  const [semanaFin, setSemanaFin] = useState(valoresIniciales.semana);
  const [datos, setDatos] = useState<DTOReporteHorarioTE[]>([]);
  const [filtro, setFiltro] = useState('');
  const [cargando, setCargando] = useState(false);
  const [showMsg, setShowMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [header, setHeader] = useState('');
  const [msgColor, setMsgColor] = useState('primary');

  const mostrarMensaje = useCallback(
    (encabezado: string, mensaje: string, color: string) => {
      setHeader(encabezado);
      setMsg(mensaje);
      setMsgColor(color);
      setShowMsg(true);
    },
    []
  );

  const validarParametros = (
    cicloConsulta: number,
    inicioConsulta: number,
    finConsulta: number
  ) => {
    if (
      !Number.isInteger(cicloConsulta) ||
      cicloConsulta < 1900 ||
      cicloConsulta > 9999
    ) {
      return 'El ciclo debe estar entre 1900 y 9999.';
    }
    if (
      !Number.isInteger(inicioConsulta) ||
      inicioConsulta < 1 ||
      inicioConsulta > 52
    ) {
      return 'La semana inicial debe estar entre 1 y 52.';
    }
    if (
      !Number.isInteger(finConsulta) ||
      finConsulta < 1 ||
      finConsulta > 52
    ) {
      return 'La semana final debe estar entre 1 y 52.';
    }
    if (finConsulta < inicioConsulta) {
      return 'La semana final no puede ser menor que la semana inicial.';
    }
    return null;
  };

  const consultarReporte = useCallback(
    async (
      cicloConsulta: number,
      inicioConsulta: number,
      finConsulta: number
    ) => {
      setCargando(true);
      setShowMsg(false);

      try {
        const response = await reportesServices.getRptHorarioTEGeneral(
          cicloConsulta,
          inicioConsulta,
          finConsulta
        );
        const resultado = Array.isArray(response.data) ? response.data : [];
        setDatos(resultado);

        if (resultado.length === 0) {
          mostrarMensaje(
            'Informativo',
            'No se encontraron registros para el ciclo y rango seleccionados.',
            'primary'
          );
        }
      } catch (error) {
        setDatos([]);
        mostrarMensaje('Error', obtenerMensajeError(error), 'danger');
      } finally {
        setCargando(false);
      }
    },
    [mostrarMensaje]
  );

  useEffect(() => {
    consultarReporte(
      valoresIniciales.ciclo,
      valoresIniciales.semana,
      valoresIniciales.semana
    );
  }, [consultarReporte]);

  const datosFiltrados = useMemo(() => {
    const busqueda = filtro.trim().toLocaleLowerCase('es-MX');
    if (!busqueda) return datos;

    return datos.filter((row) =>
      [
        row.numEmpleado,
        row.nombreEmpleado,
        row.departamento,
        row.oficina,
      ].some((valor) =>
        (valor || '').toLocaleLowerCase('es-MX').includes(busqueda)
      )
    );
  }, [datos, filtro]);

  const buscar = () => {
    const errorValidacion = validarParametros(
      ciclo,
      semanaInicio,
      semanaFin
    );
    if (errorValidacion) {
      mostrarMensaje('Validación', errorValidacion, 'warning');
      return;
    }

    consultarReporte(ciclo, semanaInicio, semanaFin);
  };

  const descargarExcel = async () => {
    if (datosFiltrados.length === 0) {
      mostrarMensaje(
        'Informativo',
        'No hay información visible para exportar.',
        'primary'
      );
      return;
    }

    try {
      await generarExcelReporteHorarioTE(
        datosFiltrados,
        ciclo,
        semanaInicio,
        semanaFin
      );
    } catch {
      mostrarMensaje(
        'Error',
        'No se pudo generar el archivo de Excel.',
        'danger'
      );
    }
  };

  return (
    <div className="p-3">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <Form>
            <Row className="g-3 align-items-end">
              <Col xs={12} sm={6} md={2}>
                <Form.Label className="small fw-bold">Ciclo</Form.Label>
                <Form.Control
                  type="number"
                  min={1900}
                  max={9999}
                  value={ciclo}
                  onChange={(e) => setCiclo(Number(e.target.value))}
                  size="sm"
                />
              </Col>
              <Col xs={12} sm={6} md={2}>
                <Form.Label className="small fw-bold">
                  Semana inicial
                </Form.Label>
                <Form.Select
                  value={semanaInicio}
                  onChange={(e) => setSemanaInicio(Number(e.target.value))}
                  size="sm"
                >
                  {SEMANAS.map((semana) => (
                    <option key={semana} value={semana}>
                      {semana}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} sm={6} md={2}>
                <Form.Label className="small fw-bold">Semana final</Form.Label>
                <Form.Select
                  value={semanaFin}
                  onChange={(e) => setSemanaFin(Number(e.target.value))}
                  size="sm"
                >
                  {SEMANAS.map((semana) => (
                    <option key={semana} value={semana}>
                      {semana}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} sm={6} md={3}>
                <Form.Label className="small fw-bold">Buscar</Form.Label>
                <Form.Control
                  type="text"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  placeholder="Empleado, nombre, departamento u oficina"
                  size="sm"
                />
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={buscar}
                    disabled={cargando}
                    className="flex-fill"
                  >
                    {cargando ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <BsSearch />
                    )}{' '}
                    Buscar
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={descargarExcel}
                    disabled={cargando || datosFiltrados.length === 0}
                    className="flex-fill"
                  >
                    <BsFileEarmarkExcel /> Excel
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0 mt-3">
        <Card.Body className="p-0">
          <DataTable
            columns={columnas}
            data={datosFiltrados}
            pagination
            paginationPerPage={15}
            paginationRowsPerPageOptions={[10, 15, 25, 50, 100]}
            dense
            striped
            highlightOnHover
            responsive
            progressPending={cargando}
            progressComponent={
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <div className="mt-2 text-muted">Consultando reporte...</div>
              </div>
            }
            noDataComponent={
              <div className="text-muted py-5">
                No se encontraron registros para mostrar.
              </div>
            }
            customStyles={{
              headCells: {
                style: {
                  backgroundColor: '#f8f9fa',
                  color: '#1a3c6d',
                  fontWeight: 'bold',
                  fontSize: '0.82rem',
                },
              },
              cells: {
                style: {
                  fontSize: '0.8rem',
                },
              },
            }}
          />
        </Card.Body>
      </Card>

      <MsgInformativo
        show={showMsg}
        header={header}
        msg={msg}
        msgColor={msgColor}
        closeToast={() => setShowMsg(false)}
      />
    </div>
  );
}
